import React, { useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import {
  intValidation,
  nonNegativeIntValidation,
  required,
  urlValidation,
} from '../../../utils/validations';
import { InputWrapper } from '../../common/input-wrapper';
import { fetcher } from '../../../services/api';
import { AlertService } from '../../../services/alert.service';
import { useModalClose } from '../../../utils/useModalClose';
import { RouterOutput, trpcSwr } from '../../../api/trpc';

import { DatePicker } from 'rsuite';

import {
  formatDateTimeValue,
  formatDateValue,
  parseDateTimeValue,
  parseRawDateValue,
} from '../../../utils/date-value';
import { useGlobalMutate } from '../../../api/useGlobalMutate';
import { MediaInput, MediaInputValue } from '../../common/media-input';
import { isCorrectDate } from '../finance/reconciliation-act-modal';
import moment from 'moment';
import { uploadFile } from '../../../services/file-service';

type Form = {
  name: string;
  image: MediaInputValue[];
  priority: number;
  linkWithFilters: string;
  finishedAt: string;
  updatedAt: string;
};

export type BannerItem =
  RouterOutput['otcBannerAdmin']['list']['items'][number];

export type ModalProps = {
  close: () => void;
  reject: () => void;
  banner?: BannerItem;
};

export const AddBanner: React.FC<ModalProps> = ({ close, reject, banner }) => {
  const [finishedAtDate, setFinishedAtDate] = useState<Date | null>(
    banner?.finishedAt ? new Date(banner.finishedAt) : null,
  );

  const {
    formState: { isSubmitting },
    handleSubmit,
    control,
  } = useForm<Form>({
    defaultValues: banner
      ? {
          name: banner.name || '',
          image: banner.image
            ? [
                {
                  type: 'exist' as const,
                  file: banner.image,
                },
              ]
            : undefined,
          priority: banner.priority,
          linkWithFilters: banner.linkWithFilters || '',
          finishedAt: formatDateTimeValue(banner.finishedAt) || '',
          updatedAt: formatDateTimeValue(banner.updatedAt, 'DD.MM.YYYY') || '',
        }
      : {},
  });

  const { mutate: globalMutate } = useGlobalMutate();

  const { trigger: triggerUpdate } =
    trpcSwr.otcBannerAdmin.update.useSWRMutation({
      fetcher,
    });
  const { trigger: triggerCreate } =
    trpcSwr.otcBannerAdmin.create.useSWRMutation({
      fetcher,
    });

  const onSubmit = handleSubmit(async (form) => {
    let imageId: string;

    if (form.image[0].type === 'new') {
      const uploadedFile = await uploadFile(form.image[0].file, true);
      imageId = uploadedFile.file.id;
    } else {
      imageId = form.image[0].file.id;
    }

    const data = {
      name: form.name,
      imageId: imageId,
      priority: Number(form.priority),
      linkWithFilters: form.linkWithFilters,
      finishedAt: parseDateTimeValue(form.finishedAt).toDate(),
    };

    if (banner?.id) {
      await triggerUpdate({
        id: banner.id,
        data: data,
      });
    } else {
      await triggerCreate(data);
    }
    AlertService.success();
    close();
    globalMutate();
  });

  const modalRef = useModalClose(reject);

  return (
    <div className="modal-dialog" ref={modalRef}>
      <form className="modal-content" onSubmit={onSubmit}>
        <div className="modal-header border-bottom-0 p-4">
          <h3>{banner?.id ? 'Редактировать' : 'Добавить'} рекламный баннер</h3>
          <button
            type="button"
            className="btn-close"
            onClick={() => reject()}
            disabled={isSubmitting}
          />
        </div>
        <div className="modal-body p-4">
          <Controller
            control={control}
            name="name"
            rules={{
              ...required,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Внутреннее название"
                error={fieldState.error?.message}
                required
              >
                <input className="form-control mb-3" type="text" {...field} />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="priority"
            rules={{ ...nonNegativeIntValidation, ...required }}
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Приоритет"
                error={fieldState.error?.message}
                required
              >
                <div className="input-group">
                  <input {...field} type="number" className="form-control" />
                </div>
                <div className="fs-14 color-gray-400">
                  Влияет на сортировку. Чем меньше, тем выше в списке
                </div>
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="image"
            rules={{ ...required }}
            render={({ field, fieldState }) => (
              <InputWrapper
                theme="light"
                className="mt-3"
                required
                title="Изображение баннера"
                error={fieldState.error?.message}
              >
                <MediaInput {...field} className="form-control" />
              </InputWrapper>
            )}
          />
          <Controller
            control={control}
            name="linkWithFilters"
            rules={{
              ...required,
              ...urlValidation,
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                title="Ссылка с фильтрами"
                className="mt-3"
                error={fieldState.error?.message}
                required
              >
                <input className="form-control" type="text" {...field} />
                <div className="fs-14 color-gray-400 mt-1">
                  https://my.express-today.ru/1688....
                </div>
              </InputWrapper>
            )}
          />
          <Controller
            name="finishedAt"
            control={control}
            rules={{
              validate: (value) => {
                if (!value) return true;

                const now = moment.utc().startOf('day');
                if (parseRawDateValue(value).isBefore(formatDateValue(now)))
                  return 'Дата окончания должна быть в будущем';

                return true;
              },
            }}
            render={({ field, fieldState }) => {
              const now = moment.utc().startOf('day');
              return (
                <InputWrapper
                  title="Дата окончания показа"
                  error={fieldState.error?.message}
                  className="mt-3"
                >
                  <DatePicker
                    className={'w-100'}
                    shouldDisableDate={(d) => {
                      return moment(d).isSameOrBefore(now, 'day');
                    }}
                    format={'dd.MM.yyyy'}
                    isoWeek={true}
                    onChange={(date) => {
                      if (date === null) {
                        setFinishedAtDate(null);
                        field.onChange(null);
                      } else {
                        setFinishedAtDate(date);
                        field.onChange(formatDateValue(moment(date)));
                      }
                    }}
                    value={finishedAtDate}
                  />
                </InputWrapper>
              );
            }}
          />
          {banner?.id && (
            <Controller
              control={control}
              name="updatedAt"
              render={({ field, fieldState }) => (
                <InputWrapper
                  title="Дата последнего обновления настроек:"
                  className="mt-3"
                  error={fieldState.error?.message}
                >
                  <div>{field.value}</div>
                </InputWrapper>
              )}
            />
          )}
        </div>
        <div className="modal-footer py-4 d-flex flex-column align-items-start">
          <button
            type="submit"
            className="mt-5 px-5 btn btn-success w-100"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              />
            ) : banner?.id ? (
              'Сохранить'
            ) : (
              'Добавить'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
